/**
 * useSiteImages — one-shot fetch of admin-configured site images so
 * marketing pages can override their default hero/mockup images.
 *
 * Response shape from GET /api/public/site-images:
 *   { images: { [slot]: "/uploads/site/xyz.jpg", ... } }
 *
 * Usage:
 *   const { imageFor } = useSiteImages();
 *   const src = imageFor('drive_hero_mockup', DEFAULT_URL);
 *
 * The hook caches the response in sessionStorage for the tab lifetime so
 * navigating between pages doesn't refetch. Admin changes take effect on
 * the next tab open (or hard refresh).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'site_images_v1';

// Absolute URL builder for `/uploads/...` values so pages can use them
// directly in <img src>.
const abs = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API}/api${url.startsWith('/') ? '' : '/'}${url}`;
};

export function useSiteImages() {
  const [images, setImages] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/site-images`)
      .then((r) => {
        if (cancelled) return;
        const data = r.data?.images || {};
        setImages(data);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
      })
      .catch(() => { /* fallback silently to defaults */ });
    return () => { cancelled = true; };
  }, []);

  const imageFor = (slot, fallback) => {
    const v = images?.[slot];
    return v ? abs(v) : fallback;
  };

  return { images, imageFor };
}

// Small helper to bust the cache after an admin uploads a new image.
export const clearSiteImagesCache = () => {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
};
